import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../../state/FavoritesContext';
import { useMarketing } from '../../state/MarketingContext';
import { useAuth } from '../../state/auth';
import { usePartner } from '../../state/PartnerContext';
import { SimpleHeader } from '../../components/SimpleHeader';
import { AdvancedFilters } from './components/AdvancedFilters';
import { useProductFiltering } from '../../hooks/useProductFiltering';
import { useCatalogData } from './hooks/useCatalogData';
import { CatalogFilters } from './components/CatalogFilters';
import { CatalogFeed } from './components/CatalogFeed';

export const CatalogScreen = React.memo(() => {
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const { categories } = useMarketing();
  const { user } = useAuth();
  const { followingShops } = usePartner();
  
  const {
    // Data
    allProducts,
    shops,
    loading,
    loadingMore,
    hasMore,
    
    // Filters & Search
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedShop,
    setSelectedShop,
    showAdvancedFilters,
    setShowAdvancedFilters,
    advancedFilters,
    
    // UI State
    stableSets,
    mainScrollRef,
    filterScrollRef,
    showLeftArrow,
    showRightArrow,
    
    // Computed
    isFavoritesView,
    hasFavorites,
    filterCategories,
    totalItems,
    
    // Handlers
    handleLoadMore,
    handleSelectFilter,
    scrollFilters,
    checkFilterScroll,
    handleFiltersChange
  } = useCatalogData();

  // Client-side filtering for Favorites or Search
  const { displayedProducts: rawDisplayedProducts, crossCategoryMatches } = useProductFiltering({ 
    products: isFavoritesView && hasFavorites ? allProducts.filter(p => favorites.includes(p.id)) : allProducts,
    initialCategory: filter,
    externalFilter: filter,
    externalSearch: searchQuery,
    externalSort: sortBy
  });

  // Filter by shop if selected
  const displayedProducts = useMemo(() => {
    if (!selectedShop) return rawDisplayedProducts;
    return rawDisplayedProducts.filter(p => p.shopId === selectedShop);
  }, [rawDisplayedProducts, selectedShop]);

  const handleNavigateToProduct = useCallback((productId: string) => {
    navigate(`/product/${productId}`);
  }, [navigate]);

  const handleShowAllCatalog = useCallback(() => {
    setFilter('Todos');
    setSearchQuery('');
  }, [setFilter, setSearchQuery]);

  const isFavoritesEmpty = isFavoritesView && !hasFavorites;

  return (
    <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
      <div className="sticky top-0 z-40 bg-background-light dark:bg-background-dark shadow-sm">
        <SimpleHeader title={isFavoritesView ? "Mis Favoritos" : "Catálogo"} showBack={isFavoritesView} />
        
        {/* Hide Filters if viewing Favorites List (Clean UI) - But show if Favorites is Empty (To Explore) */}
        {(!isFavoritesView || !hasFavorites) && (
          <CatalogFilters
            filter={filter}
            setFilter={setFilter}
            selectedShop={selectedShop}
            setSelectedShop={setSelectedShop}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            filterCategories={filterCategories}
            shops={shops}
            totalItems={displayedProducts.length}
            showLeftArrow={showLeftArrow}
            showRightArrow={showRightArrow}
            filterScrollRef={filterScrollRef}
            onScroll={checkFilterScroll}
            onScrollFilters={scrollFilters}
            setShowAdvancedFilters={setShowAdvancedFilters}
            isFavoritesView={isFavoritesView}
            hasFavorites={hasFavorites}
            user={user}
            followingShops={followingShops}
          />
        )}
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 pb-32 no-scrollbar" ref={mainScrollRef}>
        <CatalogFeed
          displayedProducts={displayedProducts}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          filter={filter}
          stableSets={stableSets}
          categories={categories}
          mainScrollRef={mainScrollRef}
          setFilter={setFilter}
          setSearchQuery={setSearchQuery}
          handleLoadMore={handleLoadMore}
          isFavoritesView={isFavoritesView}
          hasFavorites={hasFavorites}
          searchQuery={searchQuery}
          crossCategoryMatches={crossCategoryMatches}
          allProducts={allProducts}
          handleSelectFilter={handleSelectFilter}
          handleShowAllCatalog={handleShowAllCatalog}
          handleNavigateToProduct={handleNavigateToProduct}
        />
      </div>

      {showAdvancedFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAdvancedFilters(false)}>
          <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <AdvancedFilters
              availableCategories={filterCategories.filter(c => c !== 'Todos')}
              onFiltersChange={handleFiltersChange}
              onClose={() => setShowAdvancedFilters(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
});

CatalogScreen.displayName = 'CatalogScreen';
